import type { KyInstance } from "ky";
import {
	aplicarTokenRotacionadoSpaceApi,
	atualizarTokenUmaVezSpaceApi,
	getTokenJwtSpaceApi,
	notificarFalhaAutenticacaoSpaceApi,
} from "./sessaoAutenticacao.space";

function origemConfiavel(url: string, origensConfiaveis?: string[]): boolean {
	if (!origensConfiaveis?.length) return true;

	try {
		const origem = new URL(url).origin;
		return origensConfiaveis.includes(origem);
	} catch {
		return false;
	}
}

function deveTentarNovamente(requisicao: Request, resposta: Response, origensConfiaveis?: string[]): boolean {
	if (requisicao.url.includes("/refresh")) return false;
	if (resposta.status !== 401) return false;
	if (requisicao.headers.get("x-auth-retry") === "1") return false;

	return origemConfiavel(requisicao.url, origensConfiaveis);
}

export function comAutenticacaoSpace(instancia: KyInstance, origensConfiaveis?: string[]): KyInstance {
	return instancia.extend({
		hooks: {
			beforeRequest: [
				(requisicao) => {
					if (!origemConfiavel(requisicao.url, origensConfiaveis)) return;

					const tokenAtual = getTokenJwtSpaceApi();
					if (tokenAtual && !requisicao.headers.has("Authorization"))
						requisicao.headers.set("Authorization", `Bearer ${tokenAtual}`);
				},
			],
			afterResponse: [
				async (requisicao, opcoes, resposta) => {
					if (origemConfiavel(requisicao.url, origensConfiaveis)) {
						const tokenRotacionado = resposta.headers.get("New-Access-Token");
						if (tokenRotacionado) aplicarTokenRotacionadoSpaceApi(tokenRotacionado);
					}

					if (!deveTentarNovamente(requisicao, resposta, origensConfiaveis)) return resposta;

					const novoToken = await atualizarTokenUmaVezSpaceApi();
					if (!novoToken) {
						notificarFalhaAutenticacaoSpaceApi();
						return resposta;
					}

					const cabecalhosRetry = new Headers(requisicao.headers);
					cabecalhosRetry.set("Authorization", `Bearer ${novoToken}`);
					cabecalhosRetry.set("x-auth-retry", "1");

					const requisicaoRetry = new Request(requisicao, {
						headers: cabecalhosRetry,
					});

					return instancia(requisicaoRetry, opcoes);
				},
			],
		},
	});
}

export default comAutenticacaoSpace;

/**
 * Equivalente ao `comAutenticacaoSpace` (ky) para chamadas com `fetch` direto.
 * Injeta token, trata 401 com refresh, e aplica rotação de token.
 */
export async function fetchComRefreshToken(url: string, opcoes: RequestInit): Promise<Response> {
	const cabecalhos = new Headers(opcoes.headers);

	const tokenAtual = getTokenJwtSpaceApi();
	if (tokenAtual && !cabecalhos.has("Authorization")) cabecalhos.set("Authorization", `Bearer ${tokenAtual}`);

	const resposta = await fetch(url, { ...opcoes, headers: cabecalhos });

	const tokenRotacionado = resposta.headers.get("New-Access-Token");
	if (tokenRotacionado) aplicarTokenRotacionadoSpaceApi(tokenRotacionado);

	if (resposta.status !== 401) return resposta;

	const novoToken = await atualizarTokenUmaVezSpaceApi();
	if (!novoToken) {
		notificarFalhaAutenticacaoSpaceApi();
		return resposta;
	}

	const cabecalhosRetry = new Headers(opcoes.headers);
	cabecalhosRetry.set("Authorization", `Bearer ${novoToken}`);
	cabecalhosRetry.set("x-auth-retry", "1");

	return fetch(url, { ...opcoes, headers: cabecalhosRetry });
}
